import { z } from 'zod'

// ─── Enums (re-export uit prisma — centrale plek) ──────────────────────────
export const OUTGOING_EMAIL_STATUS = [
  'QUEUED', 'SENDING', 'SENT', 'FAILED', 'RETRY_PENDING', 'BOUNCED', 'DROPPED_ALLOWLIST',
] as const
export type OutgoingEmailStatus = (typeof OUTGOING_EMAIL_STATUS)[number]

export const EMAIL_TRIGGER_KEY = [
  'AI_RAPPORTAGE_DAGUPDATE',
  'KLANTPORTAAL_WELKOM',
  'KLANTPORTAAL_PASSWORD_RESET',
  // Track-I (klantenportaal-audit) — Gap D: mail naar klant bij
  // aanvraag-status AFGEWEZEN/AFGEROND.
  'KLANTPORTAAL_AANVRAAG_AFGEWEZEN',
  'KLANTPORTAAL_AANVRAAG_AFGEROND',
  'WERKBON_NAAR_KLANT',
  'FACTUUR_NAAR_KLANT',
  // FO-311 (batch-34) — klantwaardige automatisering-melding (onderhoudscontrole /
  // mail-automatisering) naar een klant-contactpersoon of klantenportaal, onder
  // het per-klant plafond (ClientEmailNotificationSetting). Alleen via
  // mailService.send met overrides; geen EmailTrigger-template nodig.
  'AUTOMATISERING_MELDING_KLANT',
  // FO-304-G (batch-33) — klant-beoordeling-dagbundel + wekelijkse reminder.
  'KLANT_BEOORDELING_KLAARSTAAND',
  'KLANT_BEOORDELING_REMINDER',
  // FO-304-I — klant-contract-afloopmail.
  'KLANT_CONTRACT_AFLOOP',
  'VERLOF_AANGEVRAAGD',
  'VERLOF_GOEDGEKEURD',
  'VERLOF_AFGEWEZEN',
  'VERZUIM_GEMELD',
  'VERZUIM_HERSTELD',
  'ONBOARDING_WELKOM_LINK',
  // F3.7 — onboarding-flow event-mails
  'ONBOARDING_STAP_GOEDGEKEURD',
  'ONBOARDING_STAP_AFGEWEZEN',
  'ONBOARDING_AFGEROND',
  'ONBOARDING_REMINDER',
  'ONBOARDING_DEADLINE_HR',
  // Spec C §3.3 / §11 — 4-ogencontrole goedkeur/afkeur event-mails (legacy,
  // dormant sinds spec 25 — 4-ogencontrole vervalt; triggers blijven staan
  // voor in-flight legacy-onboardings).
  'ONBOARDING_4OGEN_GOEDGEKEURD',
  'ONBOARDING_4OGEN_AFGEKEURD',
  // Spec 25 §5.4 — fase 3/5: contract klaarstand + activatie-bericht.
  'ONBOARDING_CONTRACT_KLAAR',
  'ONBOARDING_GEACTIVEERD',
  // Medewerker werkaccount-activatie (backoffice): welkomstmail met
  // inloggegevens + app-installatie-uitleg naar het privé-adres.
  'MEDEWERKER_WERKACCOUNT_WELKOM',
  'NOTIFICATIE_HOGE_PRIO',
  'NOTIFICATIE_OPERATIONEEL',
  'NOTIFICATIE_AANKONDIGING',
  'NOTIFICATIE_REMINDER',
  // batch-41 dossier (FO-358) — HR vraagt informatie op bij de medewerker.
  // Werk-mail met privé-fallback via toOverride; reply-to = geen inbox
  // (aanleveren kan alleen via de portal, nooit per mail-reply).
  'INFORMATIE_OPGEVRAAGD',
  'INFORMATIE_VERZOEK_HERINNERING',
  // batch-61 welkomstmail (FO-448) — "Welkomstmail + inlog delen": klant-
  // account (eenmalige wachtwoord-instellen-link, NL/EN) en medewerker
  // (portal-URL + Entra-inloguitleg). Nooit een wachtwoord in de mail.
  'KLANTPORTAAL_WELKOMSTMAIL_INLOG',
  'MEDEWERKER_WELKOMSTMAIL_INLOG',
  // batch-70 voertuig-meldingen (FO-527) — de melding van een voertuigmutatie
  // aan de leasemaatschappij (Wassink) en aan de salarisverwerker (NAW). Template
  // in plaats van een bodyHtmlOverride; ontvangers uit het leverancier-record en
  // de loonstroken-instellingen, nooit een adres in code.
  'VOERTUIG_MUTATIE_LEASE',
  'VOERTUIG_MUTATIE_SALARIS',
] as const
export type EmailTriggerKey = (typeof EMAIL_TRIGGER_KEY)[number]

export const RECIPIENT_RESOLVER_KEYS = [
  'client.email',
  'actor.teamleider',
  'actor.teamleider+hr',
  'subject.user',
  'werkbon.cc',
  'all-hr',
] as const
export type RecipientResolverKey = (typeof RECIPIENT_RESOLVER_KEYS)[number]

// ─── Settings DTO ──────────────────────────────────────────────────────────
export const emailSettingsDtoSchema = z.object({
  id: z.string(),
  fromName: z.string(),
  defaultReplyTo: z.string().email(),
  corporateSignatureHtml: z.string(),
  brandPrimaryColor: z.string(),
  brandTextColor: z.string(),
  brandLogoUrl: z.string().nullable(),
  externVerzendenActief: z.boolean(),
  allowlist: z.array(z.string().email()),
  maxRetries: z.number().int().min(0).max(10),
  retryBackoffMs: z.number().int().min(1000),
  retentieDagen: z.number().int().min(1).max(3650),
})
export type EmailSettingsDTO = z.infer<typeof emailSettingsDtoSchema>

export const emailSettingsUpdateSchema = emailSettingsDtoSchema
  .partial()
  .omit({ id: true, externVerzendenActief: true, allowlist: true })

export const allowlistMutationSchema = z.object({
  add: z.array(z.string().email()).default([]),
  remove: z.array(z.string().email()).default([]),
})

// ─── Template DTO ──────────────────────────────────────────────────────────
export const emailTemplateListRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  actieveVersie: z.number().int().nullable(),
  triggerCount: z.number().int(),
  laatstGewijzigdOp: z.string().datetime().nullable(),
  laatstGewijzigdDoor: z.string().nullable(),
})
export type EmailTemplateListRowDTO = z.infer<typeof emailTemplateListRowSchema>

export const emailTemplateVersieDtoSchema = z.object({
  versie: z.number().int(),
  isActief: z.boolean(),
  subject: z.string(),
  bodyHtml: z.string(),
  bodyText: z.string(),
  createdById: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
})
export type EmailTemplateVersieDTO = z.infer<typeof emailTemplateVersieDtoSchema>

export const emailTemplateDtoSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  variabelenSchema: z.record(z.string(), z.unknown()),
  replyTo: z.string().email().nullable(),
  bijlagenToegestaan: z.boolean(),
  versies: z.array(emailTemplateVersieDtoSchema),
})
export type EmailTemplateDTO = z.infer<typeof emailTemplateDtoSchema>

export const emailTemplateCreateSchema = z.object({
  key: z.string().regex(/^[a-z0-9-]+$/, 'lowercase, cijfers, koppelteken'),
  name: z.string().min(3),
  description: z.string().optional(),
  variabelenSchema: z.record(z.string(), z.unknown()).default({}),
  replyTo: z.string().email().optional(),
  bijlagenToegestaan: z.boolean().default(false),
  subject: z.string().min(1),
  bodyHtml: z.string().min(1),
})

export const emailTemplateVersiePublishSchema = z.object({
  subject: z.string().min(1),
  bodyHtml: z.string().min(1),
  bodyText: z.string().min(1),
})

export const emailTemplateTestSchema = z.object({
  to: z.string().email(),
  testData: z.record(z.string(), z.unknown()).default({}),
})

// ─── Trigger DTO ──────────────────────────────────────────────────────────

// FO-106 — soort automatisering (op EmailTrigger vrijwel altijd EVENT_MAIL).
export const EMAIL_TRIGGER_TYPE = ['EVENT_MAIL', 'GEPLANDE_MAIL', 'ACTIE'] as const
export type EmailTriggerType = (typeof EMAIL_TRIGGER_TYPE)[number]

// Verzendmodus-waarden inline (de canonieke `MAIL_VERZEND_MODUS`/`MailVerzendModus`
// leven in ./mail-automatisering; hier alleen de literal-set voor het Zod-schema
// om een dubbele export te vermijden).
const VERZEND_MODUS_VALUES = ['AUTO', 'CONCEPT_EERST'] as const

export const emailTriggerDtoSchema = z.object({
  key: z.enum(EMAIL_TRIGGER_KEY),
  name: z.string(),
  description: z.string(),
  templateKey: z.string(),
  enabled: z.boolean(),
  recipientResolver: z.enum(RECIPIENT_RESOLVER_KEYS),
  defaultBcc: z.array(z.string().email()),
  // FO-106 — beheer-velden.
  type: z.enum(EMAIL_TRIGGER_TYPE).default('EVENT_MAIL'),
  mandatory: z.boolean().default(false),
  verzendModus: z.enum(VERZEND_MODUS_VALUES).nullable().optional(),
  mailAutomatiseringId: z.string().nullable().optional(),
  // FO-156 — open-tracking-pixel per trigger. DEFAULT UIT (AVG-beleidsbesluit);
  // alleen instelbaar op de trackbare klant-triggers (zie mail-tracking.ts).
  trackOpens: z.boolean().default(false),
})
export type EmailTriggerDTO = z.infer<typeof emailTriggerDtoSchema>

// Update: alle velden optioneel, GEEN defaults. BEWUST hand-geschreven en NIET
// `emailTriggerDtoSchema.partial()`: in Zod 4 behoudt `.partial()` de `.default()`
// van de bron-velden (zie repo-dev-gotchas / de batch-16 employees-PATCH-bug),
// waardoor `type`/`mandatory` bij élke PATCH stil terug-defaulten en de
// mandatory-kill-switch-guard (`patch.mandatory ?? bestaand.mandatory`) breekt.
// Een niet-meegestuurd veld MOET hier `undefined` blijven.
export const emailTriggerUpdateSchema = z
  .object({
    name: z.string().optional(),
    description: z.string().optional(),
    templateKey: z.string().optional(),
    enabled: z.boolean().optional(),
    recipientResolver: z.enum(RECIPIENT_RESOLVER_KEYS).optional(),
    defaultBcc: z.array(z.string().email()).optional(),
    type: z.enum(EMAIL_TRIGGER_TYPE).optional(),
    mandatory: z.boolean().optional(),
    // Gereserveerd: concept-eerst voor EVENT-mails wordt nog niet gehonoreerd in
    // `MailService.fromTrigger` (vereist een OutgoingEmail-concept-state); de UI
    // exposeert dit dus niet. Geplande mails (FO-058) honoreren verzendModus wel.
    verzendModus: z.enum(VERZEND_MODUS_VALUES).nullable().optional(),
    mailAutomatiseringId: z.string().nullable().optional(),
    // FO-156 — open-tracking aan/uit. De API weigert `true` op een trigger
    // buiten de trackbare set (interne/medewerker-mails nooit tracken).
    trackOpens: z.boolean().optional(),
  })
  .strict()

// ─── FO-106 — overkoepelend "Automatiseringen"-overzicht ─────────────────────
//
// Eén lijst over drie bronnen: event-mails (EmailTrigger), geplande mails
// (MailAutomatisering, FO-058) en acties/signalen (NotificationRule, FO-014).
export const AUTOMATISERING_BRON = ['TRIGGER', 'AUTOMATISERING', 'RULE'] as const
export type AutomatiseringBron = (typeof AUTOMATISERING_BRON)[number]

export const AUTOMATISERING_TYPE = ['EVENT_MAIL', 'GEPLANDE_MAIL', 'ACTIE'] as const
export type AutomatiseringType = (typeof AUTOMATISERING_TYPE)[number]

export const AUTOMATISERING_TYPE_LABELS: Record<AutomatiseringType, string> = {
  EVENT_MAIL: 'Event-mail',
  GEPLANDE_MAIL: 'Geplande mail',
  ACTIE: 'Actie / signaal',
}

export interface AutomatiseringDTO {
  bron: AutomatiseringBron
  /** EmailTrigger.key | MailAutomatisering.id | NotificationRule.id. */
  id: string
  naam: string
  type: AutomatiseringType
  /** Afgeleid domein (Werkbonnen / Facturen / Verlof / ...). */
  domein: string
  enabled: boolean
  mandatory: boolean
  /** templateKey / sjabloon-naam, null bij acties zonder sjabloon. */
  sjabloon: string | null
  /** Ontvanger-resolver of -samenvatting. */
  ontvanger: string | null
  /** Ritme bij geplande mails (bv "wekelijks - woensdag"); null anders. */
  ritme: string | null
  /** AUTO | CONCEPT_EERST (spiegel van MailVerzendModus); null bij acties. */
  verzendModus: 'AUTO' | 'CONCEPT_EERST' | null
  /** Afgaat-/verzend-frequentie in het venster. */
  recentCount: number
  recentWindowDays: number
}

// ─── FO-074 — reden-categorie (afgeleid uit status + lastError, geen kolom) ───
//
// Eén leesbare reden-categorie per uitgaande mail. De bron blijft `lastError`
// (technische string); deze mapping vertaalt status + `lastError`-patroon naar
// een NL categorie zodat de UI niet hoeft te raden. Bewust AFGELEID (geen
// gedenormaliseerde kolom): één waarheid, geen migratie, en een herberekening
// volgt automatisch als de worker `lastError`/status bijwerkt.
export const OUTGOING_EMAIL_REDEN_CATEGORIE = [
  'ALLOWLIST_UIT',        // extern verzenden uit + adres niet in allowlist
  'GEEN_GRAPH_CONFIG',    // log-driver actief: niet echt verzonden
  'EXTERN_GEWEIGERD_4XX', // mailserver weigerde permanent (4xx, geen 429)
  'THROTTLE_5XX',         // tijdelijke fout (429/5xx/netwerk), opnieuw proberen
  'TRIGGER_UIT',          // het mailtype staat uit (EmailTrigger.enabled=false)
  'KLANT_UIT',            // de klant heeft dit event uitgezet
  'GEEN_ONTVANGERS',      // resolver/override leverde 0 ontvangers
  'BOUNCED',              // non-delivery na acceptatie (NDR)
  'ONBEKENDE_FOUT',       // gefaald zonder herkenbaar patroon
] as const
export type OutgoingEmailRedenCategorie = (typeof OUTGOING_EMAIL_REDEN_CATEGORIE)[number]

export const OUTGOING_EMAIL_REDEN_LABELS: Record<OutgoingEmailRedenCategorie, string> = {
  ALLOWLIST_UIT:        'Extern verzenden uit (allowlist)',
  GEEN_GRAPH_CONFIG:    'Niet echt verzonden (log-driver)',
  EXTERN_GEWEIGERD_4XX: 'Geweigerd door mailserver',
  THROTTLE_5XX:         'Tijdelijke fout (opnieuw proberen)',
  TRIGGER_UIT:          'Mailtype uitgezet',
  KLANT_UIT:            'Uitgezet voor klant',
  GEEN_ONTVANGERS:      'Geen ontvangers',
  BOUNCED:              'Onbestelbaar (bounce)',
  ONBEKENDE_FOUT:       'Onbekende fout',
}

/**
 * Sentinel-prefix die de `LogMailDriver` op `graphMessageId` zet zodat het
 * overzicht "niet echt verzonden (log-driver)" kan tonen i.p.v. een misleidende
 * `SENT`. `LOG_DRIVER_LEGACY_PREFIX` dekt historische rijen (oude `log-stub-`).
 */
export const LOG_DRIVER_MESSAGE_ID_PREFIX = 'log-driver'
const LOG_DRIVER_LEGACY_PREFIX = 'log-stub'

export function isLogDriverMessageId(graphMessageId: string | null | undefined): boolean {
  if (!graphMessageId) return false
  return (
    graphMessageId.startsWith(LOG_DRIVER_MESSAGE_ID_PREFIX) ||
    graphMessageId.startsWith(LOG_DRIVER_LEGACY_PREFIX)
  )
}

export interface RedenCategorieInput {
  status: OutgoingEmailStatus
  lastError: string | null
  graphMessageId: string | null
}

/**
 * Leid de reden-categorie af uit status + `lastError`-patroon + log-driver-
 * sentinel. Retourneert `null` voor een echte `SENT`/`QUEUED`/`SENDING` (geen
 * reden nodig). De API-zijde spiegelt deze takken in `redenCategorieWhere` zodat
 * filteren en het getoonde label altijd overeenstemmen.
 */
export function bepaalRedenCategorie(
  input: RedenCategorieInput,
): OutgoingEmailRedenCategorie | null {
  const { status, graphMessageId } = input
  const err = input.lastError ?? ''

  switch (status) {
    case 'BOUNCED':
      return 'BOUNCED'
    case 'DROPPED_ALLOWLIST':
      if (err.includes('staat uit voor klant')) return 'KLANT_UIT'
      if (err.includes('recipients')) return 'GEEN_ONTVANGERS'
      if (err.includes('Trigger') && err.includes('staat uit')) return 'TRIGGER_UIT'
      return 'ALLOWLIST_UIT'
    case 'RETRY_PENDING':
      // Alleen soft-fails (429/5xx/netwerk) belanden op RETRY_PENDING; een
      // hard-fail (4xx) gaat direct naar FAILED. Dus altijd "tijdelijk".
      return 'THROTTLE_5XX'
    case 'FAILED':
      if (err.includes('faalde: 4') && !err.includes('faalde: 429')) return 'EXTERN_GEWEIGERD_4XX'
      if (err.includes('faalde: 429') || err.includes('faalde: 5')) return 'THROTTLE_5XX'
      return 'ONBEKENDE_FOUT'
    case 'SENT':
      return isLogDriverMessageId(graphMessageId) ? 'GEEN_GRAPH_CONFIG' : null
    case 'QUEUED':
    case 'SENDING':
      return null
    default:
      return null
  }
}

/** Drempel waarboven een wachtrij (QUEUED + RETRY_PENDING) als "stuw" geldt. */
export const MAIL_QUEUE_WARN_THRESHOLD = 50

/**
 * Statische SPF/DKIM/DMARC-controlelijst voor het health-blok. Of mail
 * daadwerkelijk in de inbox aankomt hangt af van deze DNS-records op streep.nl;
 * dat is operator-/DNS-config (buiten de app). Een groene `SENT` is dus niet
 * hetzelfde als "in de inbox" - deze lijst maakt dat expliciet.
 */
export const MAIL_DNS_CHECKLIST: ReadonlyArray<{
  record: string
  omschrijving: string
  voorbeeld: string
}> = [
  {
    record: 'SPF',
    omschrijving: 'Sta toe dat Microsoft 365 namens streep.nl verstuurt.',
    voorbeeld: 'v=spf1 include:spf.protection.outlook.com -all',
  },
  {
    record: 'DKIM',
    omschrijving: 'Onderteken uitgaande mail (twee CNAME-selectors via Microsoft 365).',
    voorbeeld: 'selector1._domainkey CNAME selector1-streep-nl._domainkey.<tenant>.onmicrosoft.com',
  },
  {
    record: 'DMARC',
    omschrijving: 'Bepaal hoe ontvangers omgaan met mail die SPF/DKIM niet haalt.',
    voorbeeld: 'v=DMARC1; p=quarantine; rua=mailto:dmarc@streep.nl',
  },
]

// ─── OutgoingEmail DTO ─────────────────────────────────────────────────────
export const outgoingEmailListRowSchema = z.object({
  id: z.string(),
  status: z.enum(OUTGOING_EMAIL_STATUS),
  // Afgeleide reden-categorie (NL), `null` voor een probleemloze SENT/QUEUED.
  reden: z.enum(OUTGOING_EMAIL_REDEN_CATEGORIE).nullable(),
  triggerKey: z.enum(EMAIL_TRIGGER_KEY).nullable(),
  to: z.array(z.string()),
  subject: z.string(),
  attempts: z.number().int(),
  triggerEntityType: z.string().nullable(),
  triggerEntityId: z.string().nullable(),
  queuedAt: z.string().datetime(),
  sentAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
  // FO-156 — open-tracking-samenvatting. `openTracking` = deze mail bevat een
  // pixel (tracking stond aan bij verzenden); de token zelf blijft server-side.
  // "Waarschijnlijk geopend": indicatie, geen leesbevestiging (proxy's/
  // geblokkeerde afbeeldingen vertekenen).
  openTracking: z.boolean(),
  firstOpenedAt: z.string().datetime().nullable(),
  lastOpenedAt: z.string().datetime().nullable(),
  openCount: z.number().int(),
})
export type OutgoingEmailListRowDTO = z.infer<typeof outgoingEmailListRowSchema>

export const outgoingEmailDetailSchema = outgoingEmailListRowSchema.extend({
  fromName: z.string(),
  fromAddress: z.string(),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  replyTo: z.string().nullable(),
  bodyHtml: z.string(),
  bodyText: z.string(),
  templateKey: z.string().nullable(),
  templateVersie: z.number().int().nullable(),
  lastError: z.string().nullable(),
  graphMessageId: z.string().nullable(),
  bijlagen: z.array(z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
  })),
})
export type OutgoingEmailDetailDTO = z.infer<typeof outgoingEmailDetailSchema>

// ─── Generieke "verzonden mail bekijken"-modal + toegang-audit ──────────────

/**
 * Metadata van een verzonden mail voor de generieke bekijk-modal — ZONDER body.
 * De inhoud komt apart via een geaudite, gegate endpoint (VerzondenMailInhoudDTO),
 * zodat de body nooit langs de gate lekt.
 */
export interface VerzondenMailMetaDTO {
  id: string
  status: OutgoingEmailListRowDTO['status']
  to: string[]
  replyTo: string | null
  fromName: string
  fromAddress: string
  subject: string
  attempts: number
  lastError: string | null
  queuedAt: string
  sentAt: string | null
  failedAt: string | null
}

/** De INHOUD (body) van een verzonden mail — alleen via een geaudit endpoint. */
export interface VerzondenMailInhoudDTO {
  bodyHtml: string
  bodyText: string
}

/** Een rij uit de mail-toegang-audit (admin-inzage-scherm). */
export interface EmailAccessAuditRowDTO {
  id: string
  performedBy: string
  performerNaam: string | null
  outgoingEmailId: string
  mailSubject: string | null
  /** 'uitrusting' | 'mail-uitbox' */
  bron: string
  bestellingId: string | null
  ip: string | null
  createdAt: string
}

export const MAIL_STATUS_LABELS: Record<OutgoingEmailListRowDTO['status'], string> = {
  QUEUED:            'In wachtrij',
  SENDING:           'Wordt verzonden',
  SENT:              'Verzonden',
  FAILED:            'Mislukt',
  RETRY_PENDING:     'Opnieuw proberen',
  BOUNCED:           'Gebounced',
  DROPPED_ALLOWLIST: 'Geblokkeerd (allowlist)',
}

export const MAIL_STATUS_BADGE: Record<
  OutgoingEmailListRowDTO['status'],
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  QUEUED:            'warning',
  SENDING:           'info',
  SENT:              'success',
  FAILED:            'danger',
  RETRY_PENDING:     'warning',
  BOUNCED:           'danger',
  DROPPED_ALLOWLIST: 'danger',
}

export const outgoingEmailListQuerySchema = z.object({
  // FO-146 — exact-één-mail-filter voor de deeplink vanuit de
  // MAIL_NIET_BEZORGD-melding (/mail/uitbox?id=<OutgoingEmail.id>).
  id: z.string().optional(),
  status: z.enum(OUTGOING_EMAIL_STATUS).optional(),
  triggerKey: z.enum(EMAIL_TRIGGER_KEY).optional(),
  reden: z.enum(OUTGOING_EMAIL_REDEN_CATEGORIE).optional(),
  search: z.string().optional(),
  vanaf: z.string().datetime().optional(),
  totEnMet: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).default(1),
  // Cap verhoogd naar 2000 zodat de uitbox de volledige (gefilterde) set in één
  // keer kan ophalen en client-side globaal kan sorteren + pagineren.
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
})

/** Bulk "opnieuw verzenden" - een lijst OutgoingEmail-ids (MAIL.canEdit). */
export const outgoingEmailBulkRetrySchema = z.object({
  ids: z.array(z.string()).min(1).max(500),
})

// ─── FO-074 — Health-DTO (verzendconfig + queue + DNS-checklist) ─────────────
export const mailHealthDtoSchema = z.object({
  graphGeconfigureerd: z.boolean(),
  logDriverActief: z.boolean(),
  externVerzendenActief: z.boolean(),
  allowlistAantal: z.number().int(),
  wachtend: z.object({
    queued: z.number().int(),
    retryPending: z.number().int(),
  }),
  queueWaarschuwing: z.boolean(),
  queueDrempel: z.number().int(),
  laatsteVerzondenOp: z.string().datetime().nullable(),
  laatsteBounceScanOp: z.string().datetime().nullable(),
  dnsChecklist: z.array(z.object({
    record: z.string(),
    omschrijving: z.string(),
    voorbeeld: z.string(),
  })),
})
export type MailHealthDTO = z.infer<typeof mailHealthDtoSchema>

// ─── Stats DTO ─────────────────────────────────────────────────────────────
export const mailStatsDtoSchema = z.object({
  kpi: z.object({
    verzondenLaatste7Dagen: z.number().int(),
    gefaaldLaatste7Dagen: z.number().int(),
    // FO-074 — scheid "mislukt" (FAILED + BOUNCED) van "bewust gedropt"
    // (DROPPED_ALLOWLIST: allowlist/klant-uit/geen-ontvangers). Een non-prod
    // allowlist-instelling mag niet als storing in de fout-rate verschijnen.
    gebouncedLaatste7Dagen: z.number().int(),
    misluktLaatste7Dagen: z.number().int(),
    bewustGedroptLaatste7Dagen: z.number().int(),
    wachtendNu: z.number().int(),
    bounceRatePercent: z.number(),
  }),
  perDag: z.array(z.object({
    datum: z.string(),
    verzonden: z.number().int(),
    gefaald: z.number().int(),
  })),
  topTriggers: z.array(z.object({
    triggerKey: z.enum(EMAIL_TRIGGER_KEY),
    count: z.number().int(),
  })),
  perTemplate: z.array(z.object({
    templateKey: z.string(),
    verzonden: z.number().int(),
    gefaald: z.number().int(),
    bounceRate: z.number(),
    laatstGebruiktOp: z.string().datetime().nullable(),
  })),
  laatsteFouten: z.array(outgoingEmailListRowSchema),
})
export type MailStatsDTO = z.infer<typeof mailStatsDtoSchema>
